/**
 * Component Registry for Enhanced Navigation
 *
 * Maps componentPath keys from ResourceCatalog to actual React components.
 *
 * This separation allows:
 * - APP_ADMINs to configure resources without knowing internal paths
 * - Developers to refactor/reorganize components without database changes
 * - Type-safe component imports validated at build time
 *
 * @module componentRegistry
 */

import React from 'react';

// =============================================================================
// ADMIN COMPONENTS
// =============================================================================

import ClientAdmin from 'clients/admin/ClientAdmin/ClientAdmin'
import UserAdmin from 'clients/admin/UserAdmin/UserAdmin'
import UserAdminV2 from '../pages/common/admin/UserAdminV2';
import GrantAdmin from '../pages/common/admin/GrantAdmin';
import ResourceAdmin from '../pages/common/admin/ResourceAdmin';
import PeopleAdmin from '../pages/common/admin/PeopleAdmin';
import LocationAdmin from '../pages/common/admin/LocationAdmin';

// =============================================================================
// TOOL COMPONENTS
// =============================================================================

import DemoAdmin from '../pages/common/tools/DemoAdmin';

// =============================================================================
// REPORT COMPONENTS (add as needed)
// =============================================================================

// import SalesReport from '../pages/common/reports/SalesReport';
// import LaborReport from '../pages/common/reports/LaborReport';

// =============================================================================
// DASHBOARD COMPONENTS (add as needed)
// =============================================================================

import SPLHDashboard from 'pages/common/dashboards/SPLHDashboard'


// =============================================================================
// COMPONENT MAPPING REGISTRY
// =============================================================================

/**
 * Registry mapping componentPath keys to React components
 *
 * Key naming convention: PascalCase matching the component name
 * Examples: "ResourceAdmin", "GrantAdmin", "SalesReport"
 */
const componentRegistry = {
  // Admin Tools
  ClientAdmin: <ClientAdmin />,
  UserAdmin: <UserAdmin />,
  UserAdminV2: <UserAdminV2 />,
  ResourceAdmin: <ResourceAdmin />,
  GrantAdmin: <GrantAdmin />,

  // Configuration
  PeopleAdmin: <PeopleAdmin />,
  LocationAdmin: <LocationAdmin />,

  // Tools
  DemoAdmin: <DemoAdmin />,

  // Reports (examples)
  // SalesReport: <SalesReport />,
  // LaborReport: <LaborReport />,

  // Dashboards
  SPLHDashboard: <SPLHDashboard />,
};

// =============================================================================
// PUBLIC API
// =============================================================================

/**
 * Get component by componentPath key
 *
 * @param {string} componentPath - Component identifier from ResourceCatalog (e.g., 'ResourceAdmin')
 * @returns {React.Element} Component element or placeholder if not found
 *
 * @example
 * const component = getComponentByPath('ResourceAdmin');
 * // Returns: <ResourceAdmin />
 */
export function getComponentByPath(componentPath) {
  if (!componentPath) {
    console.warn('[componentRegistry] No componentPath provided');
    return <PlaceholderComponent resourceCode="UNKNOWN" />;
  }

  const component = componentRegistry[componentPath];

  if (!component) {
    console.warn(`[componentRegistry] Component not found: ${componentPath}`);
    console.warn('[componentRegistry] Available components:', Object.keys(componentRegistry));
    return <PlaceholderComponent componentPath={componentPath} />;
  }

  return component;
}

/**
 * Check if a component is registered
 *
 * @param {string} componentPath - Component identifier to check
 * @returns {boolean} True if component is registered
 */
export function hasComponent(componentPath) {
  return componentPath && componentRegistry.hasOwnProperty(componentPath);
}

/**
 * Get list of all registered component paths
 *
 * @returns {string[]} Array of registered component path keys
 */
export function getRegisteredComponents() {
  return Object.keys(componentRegistry);
}

// =============================================================================
// PLACEHOLDER COMPONENT
// =============================================================================

/**
 * Placeholder component shown when a component mapping is missing
 *
 * @param {Object} props
 * @param {string} props.componentPath - The missing component path
 */
function PlaceholderComponent({ componentPath }) {
  return (
    <div style={{
      padding: '3rem',
      maxWidth: '600px',
      margin: '2rem auto',
      backgroundColor: '#fff3cd',
      border: '1px solid #ffc107',
      borderRadius: '8px'
    }}>
      <h2 style={{ color: '#856404', marginTop: 0 }}>Component Not Configured</h2>
      <p style={{ color: '#856404' }}>
        The component <code style={{
          padding: '2px 6px',
          backgroundColor: '#fff',
          border: '1px solid #ffc107',
          borderRadius: '4px'
        }}>{componentPath}</code> has not been registered yet.
      </p>

      <h3 style={{ color: '#856404', marginTop: '1.5rem' }}>Developer Instructions:</h3>
      <ol style={{ color: '#856404' }}>
        <li>Import the component in <code>src/utils/componentRegistry.js</code></li>
        <li>Add it to the <code>componentRegistry</code> object</li>
        <li>Example:
          <pre style={{
            backgroundColor: '#fff',
            padding: '12px',
            border: '1px solid #ffc107',
            borderRadius: '4px',
            marginTop: '8px',
            overflow: 'auto'
          }}>
{`import ${componentPath} from '../pages/path/to/${componentPath}';

const componentRegistry = {
  ...
  ${componentPath}: <${componentPath} />,
};`}
          </pre>
        </li>
      </ol>

      <p style={{ color: '#856404', marginTop: '1.5rem', fontSize: '0.875rem' }}>
        <strong>Available components:</strong> {getRegisteredComponents().join(', ')}
      </p>
    </div>
  );
}

export default componentRegistry;
